import * as React from "react";
import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useState, useEffect } from "react";
import { useNomaContext } from "./MapContext";
import { IFilters } from "../Map/filters";
import { useFiltersContext } from "./FiltersContext";
import { config, all, defaultOption, filterAllValues } from "../Map/config";
import { IEsriCodedValue } from "../Map/rest";
import { lvmForestry_Municipalities, lvmForestry_Regions, region_Municipalities } from "../Map/relations";

export interface ISideViewContext {
    filters: IFilters;
    setFilter: (name: string, value: string | number | (string | number)[]) => void;
    setRangeFilter: (fieldName: string, from?: number, to?: number) => void;
    onClear: () => void;
    onFilter: () => void;
    options: IFilterOptions;
    setOptions: Dispatch<SetStateAction<IFilterOptions>>;
    error: IError;
}
interface IError {
    fieldName: string;
    message: string;
}
interface IFilterOptions {
    [fieldName: string]: IEsriCodedValue[];
}

const SideViewContext = createContext<ISideViewContext>(null);

export const useSideViewContext = (): ISideViewContext => useContext(SideViewContext);

export const SideViewContextProvider: FC = (props: React.PropsWithChildren<ISideViewContext>) => {
    const { fieldInfos, setZoomToSourceFeatures, filterDefaultValues } = useNomaContext();
    const { setFilters } = useFiltersContext();

    const [sideViewFilters, setSideViewFilters] = useState<IFilters>(filterDefaultValues);
    const [options, setOptions] = useState<IFilterOptions>({});
    const [error, setError] = useState<IError>();

    useEffect(() => {
        if (fieldInfos) {
            const updatedOptions = {};
            config.sideView.filters.map((filter) => {
                const domain = fieldInfos["0"].find((info) => info.name === filter.field).domain;
                if (domain && domain.codedValues) {
                    updatedOptions[filter.field] =
                        filter.field === "RENTPURPOSE"
                            ? domain.codedValues.filter((codedValue) => codedValue.code !== 8)
                            : domain.codedValues;
                }
            });
            setOptions(updatedOptions);
        }
    }, [fieldInfos]);

    const changeOption = useCallback(
        (name: string, value: string) => {
            const updatedOptions = { ...options };
            let regionFilter = { name: "REGIONATVK", value: sideViewFilters["REGIONATVK"] };
            let municipalityFilter = { name: "MUNICIPALITYATVK", value: sideViewFilters["MUNICIPALITYATVK"] };

            if (name === "REGIONATVK") {
                const codedValues = getDefaultCodedValues("MUNICIPALITYATVK");

                if (value !== all) {
                    updatedOptions["MUNICIPALITYATVK"] = [defaultOption].concat(
                        region_Municipalities[value].map((municipalityCode) =>
                            codedValues.find((municipality) => municipality.code === municipalityCode)
                        )
                    );
                    regionFilter = { name, value };
                } else {
                    updatedOptions["MUNICIPALITYATVK"] = codedValues;
                    updatedOptions["REGIONATVK"] = getDefaultCodedValues("REGIONATVK");
                    regionFilter = { name: "REGIONATVK", value: all };
                }
                municipalityFilter = { name: "MUNICIPALITYATVK", value: all };
            }
            if (name === "MUNICIPALITYATVK") {
                if (value !== all) {
                    const regionCodedValue = getDefaultCodedValues("REGIONATVK").find(
                        (region) => region.code === getRegionByMunicipality(value)
                    );
                    // TODO sakārtot datus, lai nav tā ka pagasts nav nevienā reģionā
                    if (!regionCodedValue || !regionCodedValue.code) {
                        console.log("MUNICIPALITYATVK", value, " nav nevienā reģionā");
                        updatedOptions["REGIONATVK"] = getDefaultCodedValues("REGIONATVK");
                        regionFilter = { name: "REGIONATVK", value: all };
                    } else {
                        updatedOptions["REGIONATVK"] = [regionCodedValue, defaultOption];
                        regionFilter = { name: "REGIONATVK", value: regionCodedValue.code as string };
                    }
                }

                const allMunicipalities = getDefaultCodedValues("MUNICIPALITYATVK");

                const filteredMunicipalities =
                    typeof regionFilter.value === "string" && regionFilter.value !== all
                        ? getRegionMunicipalities(regionFilter.value, allMunicipalities)
                        : allMunicipalities;

                regionFilter.value !== all && filteredMunicipalities.unshift(defaultOption);

                updatedOptions["MUNICIPALITYATVK"] = filteredMunicipalities;
                municipalityFilter = { name, value };
            }

            setOptions(updatedOptions);

            const updatedFilters = { ...sideViewFilters };
            name !== "REGIONATVK" && name !== "MUNICIPALITYATVK" ? (updatedFilters[name] = value) : false;
            setSideViewFilters({
                ...updatedFilters,
                ["REGIONATVK"]: regionFilter.value,
                ["MUNICIPALITYATVK"]: municipalityFilter.value
            });

            validateForestry(name, value, municipalityFilter.value as string, regionFilter.value as string)
                ? setError(undefined)
                : setError({ fieldName: "LVMFORESTRYCODE", message: "Neatbilstošs kritērijs!" });
        },
        [options, fieldInfos, sideViewFilters]
    );

    const getDefaultCodedValues = (fieldName: string) =>
        fieldInfos["0"].find((i) => i.name === fieldName).domain.codedValues;

    const getRegionByMunicipality = (municipality: string) =>
        Object.keys(region_Municipalities).find((r) => region_Municipalities[r].includes(municipality as string));

    const getRegionMunicipalities = (region: string, allMunicipalities: IEsriCodedValue[]) =>
        region_Municipalities[region].map((municipality) => allMunicipalities.find((m) => m.code === municipality));

    const validateForestry = (name: string, value: string, municipality: string, region: string) => {
        const validate = (forestry: string) =>
            municipality !== all
                ? lvmForestry_Municipalities[forestry].includes(municipality)
                : region !== all
                ? lvmForestry_Regions[forestry].includes(region)
                : true;

        return name === "LVMFORESTRYCODE"
            ? value !== all
                ? validate(value)
                : true
            : sideViewFilters["LVMFORESTRYCODE"] !== all
            ? validate(sideViewFilters["LVMFORESTRYCODE"] as string)
            : true;
    };

    const setRangeFilter = useCallback(
        (fieldName, from?: number, to?: number) => {
            const filter = sideViewFilters[fieldName];
            const fromVal = from || from === 0 ? from : (filter && filter[0]) || 0;
            const toVal = to || to === 0 ? to : (filter && filter[1]) || 0;

            setSideViewFilters({ ...sideViewFilters, [fieldName]: [Number(fromVal), Number(toVal)] });
        },
        [sideViewFilters]
    );

    const onClear = useCallback(() => {
        setOptions({
            ...options,
            ["MUNICIPALITYATVK"]: getDefaultCodedValues("MUNICIPALITYATVK"),
            ["REGIONATVK"]: getDefaultCodedValues("REGIONATVK")
        });
        setError(undefined);
        setSideViewFilters(filterAllValues);
        setFilters(filterAllValues);
        setZoomToSourceFeatures(1);
    }, [sideViewFilters, options]);

    const onFilter = useCallback(() => {
        setFilters(sideViewFilters);
        setZoomToSourceFeatures(Date.now());
    }, [sideViewFilters]);

    const values = {
        filters: sideViewFilters,
        setFilter: changeOption,
        setRangeFilter,
        onClear,
        onFilter,
        options,
        setOptions,
        error
    };
    return <SideViewContext.Provider value={values}>{props.children}</SideViewContext.Provider>;
};
